// src/admin/components/access-urls/ClaimUrlModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import FormSelect from '../../../shared/components/FormSelect';
import useAdminAssets from '../../hooks/useAdminAssets';
import useAdminWorkflows from '../../hooks/useAdminWorkflows';
import ModalSearchBar from '../ModalSearchBar';
import { formatAddress, hasAddress } from '../../../shared/utils/formatAddress';

/**
 * Modal for claiming a short URL to an asset
 *
 * Optionally allows pre-assigning a User Workflow so that scanning the
 * resulting QR code launches the workflow directly with no claim step.
 */
export default function ClaimUrlModal({ show, onClose, shortUrl, onClaim }) {
  const { data: assets, refresh: refreshAssets } = useAdminAssets();
  const { data: workflows, refresh: refreshWorkflows } = useAdminWorkflows();
  const [search, setSearch] = useState('');
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setSearch('');
      setSelectedAssetId(null);
      setSelectedWorkflowId('');
      setIsSubmitting(false);
      refreshAssets();
      refreshWorkflows();
    }
  }, [show, refreshAssets, refreshWorkflows]);

  // Filter assets by search
  const filteredAssets = useMemo(() => {
    if (!search.trim()) return assets;
    const q = search.toLowerCase();
    return assets.filter(a =>
      [a.asset_name, a.asset_make, a.asset_model, a.asset_ext_id, a.street_address, a.city]
        .some(field => String(field || '').toLowerCase().includes(q))
    );
  }, [assets, search]);

  // Only published User Workflows can be wired to a short URL.
  const userWorkflowOptions = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state === 'published'),
    [workflows]
  );
  const draftUserWorkflowCount = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state !== 'published').length,
    [workflows]
  );

  const handleSubmit = async () => {
    if (!selectedAssetId) return;
    setIsSubmitting(true);
    try {
      await onClaim(selectedAssetId, selectedWorkflowId || null);
    } catch {
      // Error handling is done in parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button
        type="button"
        className="btn btn-link text-muted"
        onClick={onClose}
        disabled={isSubmitting}
      >
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSubmit}
        disabled={!selectedAssetId || isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Claiming...
          </>
        ) : (
          <>
            <i className="bi bi-link-45deg me-1" />
            Claim URL
          </>
        )}
      </button>
    </div>
  );

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title="Claim Short URL"
      footer={footer}
      size="lg"
    >
      {/* Info about the URL being claimed */}
      {shortUrl && (
        <div className="alert alert-light border mb-3">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-qr-code fs-4" />
            <div>
              <div className="small text-muted">Claiming short URL</div>
              <code className="fs-5">{shortUrl.code}</code>
            </div>
          </div>
        </div>
      )}

      <p className="text-muted mb-3">
        Select an asset to associate with this short URL. Once claimed, scanning the QR code will
        direct users to this asset's page.
      </p>

      {/* Search bar */}
      <ModalSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search assets..."
      />

      {/* Asset list */}
      <div className="list-group mt-3" style={{ maxHeight: 400, overflowY: 'auto' }}>
        {filteredAssets.length > 0 ? (
          filteredAssets.map(asset => (
            <button
              key={asset.id}
              type="button"
              className={`list-group-item list-group-item-action d-flex align-items-center gap-3 ${
                selectedAssetId === asset.id ? 'active' : ''
              }`}
              onClick={() => setSelectedAssetId(asset.id)}
            >
              <div className="flex-shrink-0">
                <i className={`bi bi-boombox fs-4 ${selectedAssetId === asset.id ? '' : 'text-muted'}`} />
              </div>
              <div className="flex-grow-1 text-start">
                <div className="fw-semibold">{asset.asset_name || 'Unnamed Asset'}</div>
                <div className={`small ${selectedAssetId === asset.id ? 'opacity-75' : 'text-muted'}`}>
                  {[asset.asset_make, asset.asset_model].filter(Boolean).join(' · ') || '—'}
                  {hasAddress(asset) && (
                    <span className="ms-2">
                      <i className="bi bi-geo-alt me-1" />
                      {formatAddress(asset, { fallback: '' })}
                    </span>
                  )}
                </div>
              </div>
              {selectedAssetId === asset.id && (
                <i className="bi bi-check-lg" />
              )}
            </button>
          ))
        ) : (
          <div className="text-center text-muted py-4">
            <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
            <p className="mb-0">
              {search ? 'No assets match your search.' : 'No assets available.'}
            </p>
          </div>
        )}
      </div>

      {/* Optional User Workflow selector */}
      {userWorkflowOptions.length > 0 && (
        <div className="mt-4">
          <FormSelect
            id="claim-workflow-select"
            label={<>Direct-launch workflow <span className="text-muted">(optional)</span></>}
            value={selectedWorkflowId}
            onChange={(e) => setSelectedWorkflowId(e.target.value)}
            placeholder="— None (regular asset access link) —"
            options={userWorkflowOptions.map((w) => ({ value: w.id, label: w.name }))}
            helperText="When a User Workflow is selected, scanning this QR code starts the workflow directly without prompting visitors for any identity."
            className="mb-0"
          />
          {draftUserWorkflowCount > 0 && (
            <div className="alert alert-warning mt-2 mb-0 py-2 small">
              <i className="bi bi-exclamation-triangle me-2" />
              {draftUserWorkflowCount === 1
                ? '1 additional User Workflow is still in draft and not selectable here. Publish it to use it as a direct-launch workflow.'
                : `${draftUserWorkflowCount} additional User Workflows are still in draft and not selectable here. Publish them to use them as direct-launch workflows.`}
            </div>
          )}
        </div>
      )}

      {userWorkflowOptions.length === 0 && draftUserWorkflowCount > 0 && (
        <div className="alert alert-warning mt-4 mb-0 small">
          <i className="bi bi-exclamation-triangle me-2" />
          {draftUserWorkflowCount === 1
            ? 'A User Workflow exists but is still in draft. Publish it to attach it to this short URL as a direct-launch workflow.'
            : `${draftUserWorkflowCount} User Workflows exist but are still in draft. Publish at least one of them to attach it to this short URL as a direct-launch workflow.`}
        </div>
      )}
    </AdminModal>
  );
}

ClaimUrlModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  shortUrl: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
  }),
  onClaim: PropTypes.func.isRequired,
};
